import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControlLabel,
	FormGroup,
	Grid2 as Grid,
	type GridSize,
	Stack,
	Switch,
	TextField,
} from "@mui/material";
import { dispatchUILoaded, dispatchUILoading } from "@reducers/uiReducer";
import { useAppSelector } from "@store/hooks";
import job from "@utils/objects/job";
import { StringProcessor } from "@utils/stringProcessor";
import React from "react";
import useDebugInformation from "../../../../../../../hooks/useDebugInformation";
import { state, ui } from "../../../../../../../interfaces/interfaces";
import MemoizedFormInput from "../../../../../../Shared/MemoizedFormInput";

const name = "FinalModal";
const prefix = "BookingFinalModal";

const handleClose = () => {
	ui.FinalDialogModal = false;
};

const handleSave = () => {
	const submit = async () => {
		state.SavingJob = true;
		ui.FinalDialogModal = false;
		dispatchUILoading();
		const result = await job.submit();

		if (result) {
			ui.ActiveStep = (ui.ActiveStep + 1) as 0 | 1 | 2 | 3;
			ui.ProblemMsg = "Booking saved.";
			ui.ProblemMsgVariant = "success";
			ui.ProblemShowMsg = true;
			state.SavingJob = false;
		} else {
			ui.ProblemMsg = "Error submitting job";
			ui.ProblemMsgVariant = "error";
			ui.ProblemShowMsg = true;
			state.JobStatusId = 99;
			state.SavingJob = false;
		}
		dispatchUILoaded();
	};
	submit();
};

const emailSwitch = (
	_e: React.SyntheticEvent<Element, Event>,
	checked: boolean,
) => {
	ui.OkToSendCommunication = checked;
	state.JobIsOkToMarket = checked;
};

const parkingSwitch = (
	_e: React.SyntheticEvent<Element, Event>,
	checked: boolean,
) => {
	ui.BookingParkingAvailability = checked;
	if (!checked) {
		ui.BookingParkingLocation = "";
	}
	state.NotesParking = `${checked ? "Yes" : "No"}`;
};

type A11yProps = (name: string) => {
	id: string;
	name: string;
	fullWidth: boolean;
	autoComplete: string;
	"aria-controls": string;
	onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export const a11yProps: A11yProps = (name) => ({
	id: `input-${name.toLowerCase()}`,
	name: `BookingParking${name}`,
	fullWidth: true,
	autoComplete: "none",
	"aria-controls": `action-input-${name.toLowerCase()}`,
	onChange: handleChange,
});

const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
	const value = event.target.value ? event.target.value : "";
	ui.BookingParkingLocation = value;
	if (value) {
		state.NotesParking = `Yes; Location: ${value}`;
	} else {
		state.NotesParking = `${ui.BookingParkingLocation ? "Yes" : "No"}`;
	}
};

type MemoizedInputProps = {
	parent: string;
	inputName: string;
	xs: GridSize;
	sm: GridSize;
	type?: string;
};

const MemoizedInput = React.memo<MemoizedInputProps>(
	({ parent, inputName, xs, sm, type }) => {
		const monitored = useAppSelector(({ ui }) => ui.BookingParkingLocation);
		useDebugInformation(`${parent}▷[${inputName}]`, {
			inputName,
			xs,
			sm,
			monitored,
		});
		return (
			<Grid
				size={{
					xs,
					sm,
				}}
			>
				{" "}
				<TextField
					value={monitored}
					required
					{...a11yProps(inputName)}
					label={StringProcessor.camelToWords(inputName)}
					type={type}
					slotProps={{
						inputLabel:
							type === "date" || type === "number" ? { shrink: true } : {},
					}}
				/>
			</Grid>
		);
	},
);

type FinalModalProps = {
	parent: string;
};

const FinalModal: React.FC<FinalModalProps> = ({ parent }) => {
	const open = useAppSelector(({ ui }) => ui.FinalDialogModal);
	const okToSendComms = useAppSelector(({ ui }) => ui.OkToSendCommunication);
	const parkingAvailability = useAppSelector(
		({ ui }) => ui.BookingParkingAvailability,
	);
	const isSavingJob = useAppSelector(({ state }) => state.SavingJob);
	const label = { inputProps: { "aria-label": "Switch demo" } };

	const debugName = `${parent}▷[${name}]`;

	useDebugInformation(debugName, { name, prefix });
	return (
		<Dialog open={open} onClose={handleClose}>
			<DialogTitle>Booking completion</DialogTitle>
			<DialogContent dividers>
				<DialogContentText>
					Please ask the customer for the following final information:
				</DialogContentText>
				<Stack spacing={1}>
					<FormGroup>
						<FormControlLabel
							control={
								<Switch
									{...label}
									checked={okToSendComms}
									onChange={emailSwitch}
								/>
							}
							label="Ok to send email with final bill and any special offers?"
						/>
					</FormGroup>
					<MemoizedFormInput
						inputName="Email"
						parent={debugName}
						prefix="PropertyContact"
						xs={12}
						sm={12}
						disabled={false}
					/>

					<FormGroup>
						<FormControlLabel
							control={
								<Switch
									{...label}
									checked={parkingAvailability}
									onChange={parkingSwitch}
								/>
							}
							label="Is parking available?"
						/>
					</FormGroup>
					{parkingAvailability && (
						<MemoizedInput
							inputName="Location"
							parent={debugName}
							xs={12}
							sm={6}
							type="text"
						/>
					)}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button color="error" variant="contained" onClick={handleClose}>
					Return
				</Button>
				<Button
					color="success"
					variant="contained"
					onClick={handleSave}
					disabled={isSavingJob}
				>
					Save Booking
				</Button>
			</DialogActions>
		</Dialog>
	);
};

FinalModal.displayName = name;

export default React.memo(FinalModal);
